/**
 * Top Services Table Component
 *
 * Displays top performing services by revenue with visual bar chart
 */

import { useTranslation } from "react-i18next";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { PriceDisplay } from "@/components/ui/price-display";
import { LockedChartCard } from "./locked-chart-card";
import { MetricTooltip } from "./metric-tooltip";

interface TopServicesTableProps {
  data: Array<{
    serviceId: string;
    serviceName: string;
    categoryName?: string;
    revenue: number;
    appointmentCount: number;
    averagePrice: number;
    revenuePercentage: number;
  }> | null;
}

export function TopServicesTable({ data }: TopServicesTableProps) {
  const { t } = useTranslation("reports");

  // If data is null (basic tier), show locked version with mock data
  if (data === null || data === undefined) {
    const mockContent = (
      <div className="space-y-2">
        {[1, 2, 3, 4, 5].map((i) => (
          <div key={i} className="flex items-center gap-3">
            {/* Mock service name */}
            <div className="flex-1 min-w-0">
              <div className="font-medium text-xs">Service Name {i}</div>
              <div className="text-[10px] text-muted-foreground">Category</div>
            </div>

            {/* Mock revenue bar */}
            <div className="flex-1 relative h-5 bg-muted rounded overflow-hidden">
              <div
                className="absolute inset-y-0 left-0 bg-gradient-to-r from-blue-500 to-blue-400"
                style={{ width: `${100 - i * 15}%` }}
              />
            </div>

            {/* Mock stats */}
            <div className="text-right">
              <div className="font-semibold text-xs whitespace-nowrap">€0,000</div>
              <div className="text-[10px] text-muted-foreground whitespace-nowrap">
                00 bookings • 0.0%
              </div>
            </div>
          </div>
        ))}
      </div>
    );

    return (
      <LockedChartCard
        title={t("charts.topServices.title")}
        tooltip={t("charts.topServices.tooltip")}
        mockContent={mockContent}
        height="auto"
      />
    );
  }

  // Sort by revenue and take top 10
  const topServices = [...data].sort((a, b) => b.revenue - a.revenue).slice(0, 10);

  if (topServices.length === 0) {
    return (
      <Card className="animate-in fade-in slide-in-from-bottom-4 duration-500 delay-150">
        <CardHeader>
          <CardTitle className="text-sm font-medium text-muted-foreground">
            {t("charts.topServices.title")}
          </CardTitle>
        </CardHeader>
        <CardContent>
          <p className="text-sm text-muted-foreground">{t("common.noServiceData")}</p>
        </CardContent>
      </Card>
    );
  }

  const maxRevenue = Math.max(...topServices.map((s) => s.revenue));

  return (
    <Card className="animate-in fade-in slide-in-from-bottom-4 duration-500 delay-150">
      <CardHeader className="pb-3">
        <CardTitle className="text-sm font-medium flex items-center">
          {t("charts.topServices.title")}
          <MetricTooltip content={t("charts.topServices.tooltip")} />
        </CardTitle>
      </CardHeader>
      <CardContent>
        <div className="space-y-2">
          {topServices.map((service, index) => {
            const barWidth = (service.revenue / maxRevenue) * 100;
            return (
              <div
                key={service.serviceId}
                className="flex items-center gap-3 animate-in fade-in slide-in-from-left-4 duration-500"
                style={{ animationDelay: `${index * 50}ms` }}
              >
                {/* Service name */}
                <div className="flex-1 min-w-0">
                  <div className="font-medium text-xs truncate">{service.serviceName}</div>
                  {service.categoryName && (
                    <div className="text-[10px] text-muted-foreground truncate">
                      {service.categoryName}
                    </div>
                  )}
                </div>

                {/* Revenue bar */}
                <div className="flex-1 relative h-5 bg-muted rounded overflow-hidden">
                  <div
                    className="absolute inset-y-0 left-0 bg-gradient-to-r from-blue-500 to-blue-400 transition-all duration-1000 ease-out"
                    style={{ width: `${barWidth}%` }}
                  />
                </div>

                {/* Stats */}
                <div className="text-right">
                  <div className="font-semibold text-xs whitespace-nowrap">
                    <PriceDisplay amount={service.revenue} />
                  </div>
                  <div className="text-[10px] text-muted-foreground whitespace-nowrap">
                    {service.appointmentCount} {t("common.bookings").toLowerCase()} •{" "}
                    {service.revenuePercentage.toFixed(1)}%
                  </div>
                </div>
              </div>
            );
          })}
        </div>
      </CardContent>
    </Card>
  );
}
